import type { CharacterDTO, CharacterAppearance, ListCharactersResponse } from "@ghostland/shared";
import { API_BASE_URL } from "../config.js";
import { session } from "../auth/session.js";

export class CharacterError extends Error {}

function authHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session.token ?? ""}`,
  };
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    if (data?.error) return data.error as string;
  } catch {
    /* ignore */
  }
  return fallback;
}

/** All of the signed-in account's characters (0..3), ordered by slot. */
export async function listCharacters(): Promise<CharacterDTO[]> {
  const res = await fetch(`${API_BASE_URL}/characters`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new CharacterError(`Failed to load characters (${res.status})`);
  const body = (await res.json()) as ListCharactersResponse;
  return body.characters ?? [];
}

export async function createCharacter(name: string, appearance: CharacterAppearance): Promise<CharacterDTO> {
  const res = await fetch(`${API_BASE_URL}/characters`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify({ name, appearance }),
  });
  if (!res.ok) throw new CharacterError(await readError(res, `Failed to create character (${res.status})`));
  return (await res.json()) as CharacterDTO;
}

/** Update a character's name and/or appearance. */
export async function updateCharacter(
  characterId: string,
  patch: { name?: string; appearance?: CharacterAppearance },
): Promise<CharacterDTO> {
  const res = await fetch(`${API_BASE_URL}/characters/${characterId}`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new CharacterError(await readError(res, `Failed to update character (${res.status})`));
  return (await res.json()) as CharacterDTO;
}

export async function deleteCharacter(characterId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/characters/${characterId}`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new CharacterError(await readError(res, `Failed to delete character (${res.status})`));
}
